import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MediaPermissions, useDevices } from 'core/hooks/useDevices';
import {
    CURRENT_JOIN_WITH_AUDIO_BUTTON_STATE,
    CURRENT_JOIN_WITH_VIDEO_BUTTON_STATE,
    LAST_USED_MIC_KEY,
    LAST_USED_SPEAKER_KEY,
    LAST_USED_SPEAKER_VOLUME,
    DEFAULT_SPEAKER_VOLUME,
} from './consts';

export type UserSettingsContent = {
    audioEnabled: boolean;
    setAudioEnabled(on: boolean): void;
    videoEnabled: boolean;
    setVideoEnabled(on: boolean): void;
    audioInputDeviceId: string | undefined;
    setAudioInputDeviceId(id: string | undefined): void;
    audioOutputDeviceId: string | undefined;
    setAudioOutputDeviceId(id: string | undefined): void;
    videoDeviceId: string | undefined;
    setVideoDeviceId(id: string | undefined): void;
    audioInputDevices: MediaDeviceInfo[];
    audioOutputDevices: MediaDeviceInfo[];
    videoDevices: MediaDeviceInfo[];
    permissions: MediaPermissions;
    speakerVolume: number;
    setSpeakerVolume(value: number): void;
};

export const UserSettingsContext = createContext<UserSettingsContent>({} as UserSettingsContent);

// Тонкая обёртка над localStorage, которая глушит исключения и приводит их к "тихим" дефолтам.
// Прямой доступ к localStorage может бросать в нескольких сценариях, и каждый из них реален в проде:
//   1. Safari Private Mode / встроенные браузеры мессенджеров — setItem кидает QuotaExceededError даже при пустом storage.
//   2. Включённая опция "Block third-party cookies/storage" или корпоративные политики — getItem/setItem бросают SecurityError.
//   3. Переполнение квоты (обычно ~5MB на origin) — setItem кидает QuotaExceededError.
//   4. Отключённое JS-хранилище в about:config / групповых политиках — обращение к самому объекту бросает ReferenceError/SecurityError.
// Без обёртки любое из этих исключений всплывает в рендер провайдера и валит всё поддерево React.
const safeStorage = {
    get: (key: string): string | null => {
        try {
            return localStorage.getItem(key);
        } catch {
            return null;
        }
    },
    set: (key: string, value: string): void => {
        try {
            localStorage.setItem(key, value);
        } catch {
            /* ignore */
        }
    },
    remove: (key: string): void => {
        try {
            localStorage.removeItem(key);
        } catch {
            /* ignore */
        }
    },
};

// Громкость трактуется как 0..1 — нормализация защищает state и storage от произвольных значений извне.
const clampVolume = (volume: number): number => Math.min(1, Math.max(0, volume));

// Если в storage лежит мусор (битый кэш, ручная правка), Number(...) даст NaN и улетит к потребителям — возвращаем дефолт.
const readInitialSpeakerVolume = (): number => {
    const raw = safeStorage.get(LAST_USED_SPEAKER_VOLUME);
    if (raw === null) return DEFAULT_SPEAKER_VOLUME;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? clampVolume(parsed) : DEFAULT_SPEAKER_VOLUME;
};

// Одна и та же логика сброса активного устройства при его отключении для микрофона и динамика.
const useResetDeviceIfDisconnected = (
    activeDeviceId: string | undefined,
    devices: MediaDeviceInfo[],
    onReset: (id: string | undefined) => void,
) => {
    useEffect(() => {
        if (!activeDeviceId || activeDeviceId === 'default') return;
        const hasDevices = devices.length > 0;
        const stillConnected = devices.some(({ deviceId }) => deviceId === activeDeviceId);
        if (hasDevices && !stillConnected) {
            onReset(undefined);
        }
    }, [activeDeviceId, devices, onReset]);
};

// Отвечает за настройки пользователя(мультимедиа устройства)
export const UserSettingsProvider = ({ children }: PropsWithChildren) => {
    const [audioEnabled, setAudioEnabledState] = useState(CURRENT_JOIN_WITH_AUDIO_BUTTON_STATE);
    const [videoEnabled, setVideoEnabledState] = useState(CURRENT_JOIN_WITH_VIDEO_BUTTON_STATE);
    // Lazy initial state: без обёртки `() => ...` localStorage.getItem дёргался бы на каждом рендере провайдера,
    // хотя React использует значение только при монтировании. Так читаем storage ровно один раз.
    const [audioInputDeviceId, setAudioInputDeviceId] = useState<string | undefined>(
        () => safeStorage.get(LAST_USED_MIC_KEY) || undefined,
    );
    const [audioOutputDeviceId, setAudioOutputDeviceId] = useState<string | undefined>(
        () => safeStorage.get(LAST_USED_SPEAKER_KEY) || undefined,
    );
    const [videoDeviceId, setVideoDeviceIdState] = useState<string | undefined>();
    const [speakerVolume, setSpeakerVolume] = useState<number>(readInitialSpeakerVolume);
    const { audioInputDevices, audioOutputDevices, videoDevices, permissions } = useDevices();

    const handleSetAudioInputDeviceId = useCallback((deviceId: string | undefined) => {
        if (deviceId) {
            safeStorage.set(LAST_USED_MIC_KEY, deviceId);
        } else {
            safeStorage.remove(LAST_USED_MIC_KEY);
        }

        setAudioInputDeviceId(deviceId);
    }, []);

    const handleSetAudioOutputDeviceId = useCallback((deviceId: string | undefined) => {
        if (deviceId) {
            safeStorage.set(LAST_USED_SPEAKER_KEY, deviceId);
        } else {
            safeStorage.remove(LAST_USED_SPEAKER_KEY);
        }

        setAudioOutputDeviceId(deviceId);
    }, []);

    const handleSetSpeakerVolume = useCallback((volume: number) => {
        const normalized = clampVolume(volume);
        safeStorage.set(LAST_USED_SPEAKER_VOLUME, String(normalized));
        setSpeakerVolume(normalized);
    }, []);

    // Узкие обёртки над setState — не пускаем наружу updater-форму Dispatch<SetStateAction>, оставляя API ровно тем, что объявлено в типе.
    const setAudioEnabled = useCallback((on: boolean) => setAudioEnabledState(on), []);
    const setVideoEnabled = useCallback((on: boolean) => setVideoEnabledState(on), []);
    const setVideoDeviceId = useCallback((id: string | undefined) => setVideoDeviceIdState(id), []);

    useResetDeviceIfDisconnected(audioInputDeviceId, audioInputDevices, handleSetAudioInputDeviceId);
    useResetDeviceIfDisconnected(audioOutputDeviceId, audioOutputDevices, handleSetAudioOutputDeviceId);

    const value = useMemo<UserSettingsContent>(
        () => ({
            audioEnabled,
            setAudioEnabled,
            videoEnabled,
            setVideoEnabled,
            audioInputDeviceId,
            setAudioInputDeviceId: handleSetAudioInputDeviceId,
            audioOutputDeviceId,
            setAudioOutputDeviceId: handleSetAudioOutputDeviceId,
            videoDeviceId,
            setVideoDeviceId,
            audioInputDevices,
            audioOutputDevices,
            videoDevices,
            permissions,
            speakerVolume,
            setSpeakerVolume: handleSetSpeakerVolume,
        }),
        [
            audioEnabled,
            setAudioEnabled,
            videoEnabled,
            setVideoEnabled,
            audioInputDeviceId,
            handleSetAudioInputDeviceId,
            audioOutputDeviceId,
            handleSetAudioOutputDeviceId,
            videoDeviceId,
            setVideoDeviceId,
            audioInputDevices,
            audioOutputDevices,
            videoDevices,
            permissions,
            speakerVolume,
            handleSetSpeakerVolume,
        ],
    );

    return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>;
};

export const useUserSettingsContext = () => useContext(UserSettingsContext);
